import template from './template.v3.2.json' with { type: "json" };
import packageInfo from '../package.json' with { type: "json" };
import { binToHex, bigIntToVmNumber, createVirtualMachineBch2026, deriveHdPublicKey, generateTransaction, hexToBin, stringifyDebugTraceSummary, summarizeDebugTrace, decodeTransactionBch, encodeTransactionBch, secp256k1, sha256, hash256, swapEndianness, deriveHdPrivateNodeChild, decodeHdPrivateKey, numberToBinUint32LE, deriveHdPublicNodeChild, deriveHdPublicNode, binToBigIntUintLE, verifyTransactionTokens, } from '@bitauth/libauth';
import { getAddress, binToBigIntUint256LE, bigIntToBinUint256LE, getChangeOutput, getLibauthCompiler, getScriptHash, getTransactionFees, getWalletInputs, sumSourceOutputTokenAmounts, sumSourceOutputValue, sumOutputValue, } from '@unspent/tau';
// PHOTON0 29972959d6f0dc766cdcb81bfaf8171c5605a64dd0a81fa46080f84ac87c9bef
export const PHOTON_CATEGORY = hexToBin('ffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff');
export const tPHOTON_CATEGORY = hexToBin('a852635be88f7291bc42e427b8107546f943e73636945a2f6cc9532af71896c0');
export default class Photon {
    static USER_AGENT = packageInfo.name;
    static tokenAware = true;
    static template = template;
    static compiler = getLibauthCompiler(this.template);
    static vm = createVirtualMachineBch2026();
    static getLockingBytecode(data = {}) {
        const lockingBytecodeResult = this.compiler.generateBytecode({
            data: data,
            scriptId: 'lock'
        });
        if (!lockingBytecodeResult.success)
            throw new Error('Failed to generate bytecode, script: , ' + JSON.stringify(lockingBytecodeResult, null, '  '));
        return lockingBytecodeResult.bytecode;
    }
    static getScriptHash(reversed = true) {
        return getScriptHash(this.getLockingBytecode(), reversed);
    }
    /**
     * Get cashaddress
     *
     * @param prefix - cashaddress prefix.
     * @throws {Error} if transaction generation fails.
     * @returns a cashaddress.
     */
    static getAddress(prefix = "bitcoincash") {
        return getAddress(this.getLockingBytecode(), prefix, this.tokenAware);
    }
    static getSourceOutput(utxo) {
        return {
            lockingBytecode: this.getLockingBytecode(),
            valueSatoshis: BigInt(utxo.value),
            token: utxo.token_data ? {
                category: hexToBin(utxo.token_data.category),
                amount: BigInt(utxo.token_data.amount),
                nft: utxo.token_data.nft ? {
                    commitment: hexToBin(utxo.token_data.nft.commitment),
                    capability: utxo.token_data.nft.capability,
                } : undefined
            } : undefined
        };
    }
    static getOracleInput(utxo) {
        return {
            outpointIndex: utxo.tx_pos,
            outpointTransactionHash: hexToBin(utxo.tx_hash),
            sequenceNumber: 0,
            unlockingBytecode: {
                data: {},
                compiler: this.compiler,
                script: 'oracle',
                valueSatoshis: BigInt(utxo.value),
                token: utxo.token_data ? {
                    category: hexToBin(utxo.token_data.category),
                    amount: BigInt(utxo.token_data.amount),
                    nft: utxo.token_data.nft ? {
                        commitment: hexToBin(utxo.token_data.nft.commitment),
                        capability: utxo.token_data.nft.capability,
                    } : undefined
                } : undefined
            },
        };
    }
    static getInput(utxo, age, minerKey) {
        return {
            outpointIndex: utxo.tx_pos,
            outpointTransactionHash: hexToBin(utxo.tx_hash),
            sequenceNumber: age,
            unlockingBytecode: {
                data: {
                    "bytecode": {
                        "age": bigIntToVmNumber(BigInt(age)),
                    },
                    hdKeys: {
                        addressIndex: 0,
                        hdPrivateKeys: {
                            'miner': minerKey
                        },
                    }
                },
                compiler: this.compiler,
                script: 'unlock',
                valueSatoshis: BigInt(utxo.value),
                token: utxo.token_data ? {
                    category: hexToBin(utxo.token_data.category),
                    amount: BigInt(utxo.token_data.amount),
                    nft: utxo.token_data.nft ? {
                        commitment: hexToBin(utxo.token_data.nft.commitment),
                        capability: utxo.token_data.nft.capability,
                    } : undefined
                } : undefined
            },
        };
    }
    static getNextTarget(utxo, now) {
        let age = utxo.height <= 0 ? 0 : now - utxo.height;
        let prevTarget = binToBigIntUint256LE(hexToBin(utxo.token_data?.nft?.commitment).slice(4, 36));
        return bigIntToBinUint256LE((prevTarget * (BigInt(age) + 143n)) / 144n);
    }
    static getOutput(utxo, amount, now, minerKey, nonce) {
        let age = utxo.height <= 0 ? 0 : now - utxo.height;
        const nextTarget = this.getNextTarget(utxo, now);
        let commitment = Uint8Array.from([
            ...numberToBinUint32LE(nonce),
            ...nextTarget
        ]);
        let msg_hash = sha256.hash(commitment);
        let parentNode = decodeHdPrivateKey(minerKey);
        if (typeof parentNode == "string")
            throw parentNode;
        let minerNodeChild = deriveHdPrivateNodeChild(parentNode.node, 0);
        let dataSig = secp256k1.signMessageHashSchnorr(minerNodeChild.privateKey, msg_hash);
        if (typeof dataSig == "string")
            throw dataSig;
        return {
            lockingBytecode: {
                data: {
                    "bytecode": {
                        "age": bigIntToVmNumber(BigInt(age)),
                    },
                    hdKeys: {
                        addressIndex: 0,
                        hdPublicKeys: {
                            'miner': deriveHdPublicKey(minerKey).hdPublicKey
                        },
                    },
                },
                compiler: this.compiler,
                script: 'lock'
            },
            valueSatoshis: BigInt(utxo.value - 1500),
            token: {
                category: hexToBin(utxo.token_data.category),
                amount: BigInt(utxo.token_data?.amount) - BigInt(amount),
                nft: {
                    capability: "mutable",
                    commitment: Uint8Array.from([...commitment, ...dataSig])
                }
            }
        };
    }
    static getOracleOutput(utxo, amount) {
        return {
            lockingBytecode: {
                data: {
                // "bytecode": {
                //     "age": bigIntToVmNumber(BigInt(0)),
                // },
                // hdKeys: {
                //     addressIndex: 0,
                //     hdPublicKeys: {
                //         'miner': deriveHdPublicKey(minerKey).hdPublicKey
                //     },
                // },
                },
                compiler: this.compiler,
                script: 'lock'
            },
            valueSatoshis: BigInt(utxo.value + amount),
            token: {
                category: hexToBin(utxo.token_data.category),
                amount: BigInt(utxo.token_data?.amount),
                nft: {
                    capability: "mutable",
                    commitment: hexToBin(utxo.token_data?.nft?.commitment)
                }
            }
        };
    }
    static getRewardOutput(amount, lockingBytecode, category = PHOTON_CATEGORY) {
        if (typeof lockingBytecode == 'string')
            lockingBytecode = hexToBin(lockingBytecode);
        return {
            lockingBytecode: lockingBytecode,
            valueSatoshis: 800n,
            token: {
                category: category,
                amount: BigInt(amount)
            }
        };
    }
    static topUp(amount, contractUtxo, walletUtxos, privateKey, addressIndex = 0, category, fee = 1) {
        const inputs = [];
        const outputs = [];
        let config = {
            locktime: 0,
            version: 2,
            inputs,
            outputs
        };
        config.inputs.push(this.getOracleInput(contractUtxo));
        config.outputs = [this.getOracleOutput(contractUtxo, amount)];
        const sourceOutputs = [this.getSourceOutput(contractUtxo)];
        const satsIn = getWalletInputs(walletUtxos, BigInt(amount), undefined, privateKey, addressIndex);
        config.inputs.push(...satsIn.inputs);
        sourceOutputs.push(...satsIn.sourceOutputs);
        // Calculate excess cash and tokens to be returned as change
        let sumSatsOut = sumOutputValue(config.outputs);
        let sumSatsIn = sumSourceOutputValue(sourceOutputs);
        let cashChange = sumSatsIn - sumSatsOut;
        config.outputs.push(getChangeOutput(cashChange, 0n, undefined, privateKey));
        let result = generateTransaction(config);
        if (!result.success)
            throw new Error('generate transaction failed!, errors: ' + JSON.stringify(result.errors, null, '  '));
        const estimatedFee = getTransactionFees(result.transaction, fee);
        const lastIdx = config.outputs.length - 1;
        config.outputs[lastIdx].valueSatoshis = config.outputs[lastIdx].valueSatoshis - estimatedFee;
        result = generateTransaction(config);
        if (!result.success)
            throw new Error('generate transaction failed!, errors: ' + JSON.stringify(result.errors, null, '  '));
        let transaction = result.transaction;
        const tokenValidationResult = verifyTransactionTokens(transaction, sourceOutputs, { maximumTokenCommitmentLength: 128 });
        if (tokenValidationResult !== true && fee > 0)
            throw tokenValidationResult;
        // Skip verification
        // let verify = this.vm.verify({
        //     sourceOutputs: sourceOutputs,
        //     transaction: transaction,
        // })
        let state = this.vm.debug({
            inputIndex: 0,
            sourceOutputs,
            transaction,
        });
        let trace = stringifyDebugTraceSummary(summarizeDebugTrace(state.slice(-9)));
        console.log(trace);
        let tokenDiff = sumSourceOutputTokenAmounts(sourceOutputs, category) -
            sumSourceOutputTokenAmounts(transaction.outputs, category);
        if (tokenDiff !== 0n)
            throw Error(`Claiming should not create or destroy tokens, token difference: ${tokenDiff}`);
        return {
            sourceOutputs: sourceOutputs,
            transaction: transaction,
            verify: false
        };
    }
    /**
     * Get transaction template for mining photons. A template is a mining transaction with a zero nonce.
     *
     * @param now - The current bitcoin block height timestamp (expressed in blocks).
     * @param contractUtxo - contract outputs to use as input.
     * @param minerThrowawayKey - A private key used for signing the nonce message.
     * @param rewardAddress - The P2PKH Cashaddress to receive payouts.
     * @param category - The token category of the photons being mined.
     * @param fee - transaction fee to pay (per byte); default 1 sat/byte.
     *
     * @throws {Error} if transaction generation fails.
     * @returns a transaction template.
     */
    static generateTemplate(now, contractUtxo, minerThrowawayKey, rewardBytecode, category, nonce = 0) {
        const inputs = [];
        const outputs = [];
        let photonCat = category ? hexToBin(category) : PHOTON_CATEGORY;
        let age = contractUtxo.height <= 0 ? 0 : now - contractUtxo.height;
        const rewardAmount = Math.floor(Number(BigInt(contractUtxo.token_data.amount) / 420000n)) - 1;
        let config = {
            locktime: 0,
            version: 2,
            inputs,
            outputs
        };
        config.inputs.push(this.getInput(contractUtxo, age, minerThrowawayKey));
        config.outputs = [this.getOutput(contractUtxo, rewardAmount, now, minerThrowawayKey, nonce)];
        config.outputs.push(this.getRewardOutput(rewardAmount, rewardBytecode, photonCat));
        let result = generateTransaction(config);
        if (!result.success)
            throw new Error('generate transaction failed!, errors: ' + JSON.stringify(result.errors, null, '  '));
        let transaction = result.transaction;
        const sourceOutputs = [this.getSourceOutput(contractUtxo)];
        // let state = this.vm.debug({
        //     inputIndex: 0,
        //     sourceOutputs,
        //     transaction,
        // })
        // let trace = stringifyDebugTraceSummary(
        //     summarizeDebugTrace(state.slice(-9)),
        // )
        // console.log(trace)
        return binToHex(encodeTransactionBch(transaction));
    }
    static getNextBatonUtxo(transactionHex) {
        let tx_id = swapEndianness(binToHex(hash256(hexToBin(transactionHex))));
        let tx = decodeTransactionBch(hexToBin(transactionHex));
        if (typeof tx == "string")
            throw tx;
        return {
            tx_pos: 0,
            tx_hash: tx_id,
            height: -1,
            value: Number(tx.outputs[0].valueSatoshis),
            token_data: {
                nft: {
                    commitment: binToHex(tx.outputs[0]?.token?.nft?.commitment),
                    capability: 'mutable'
                },
                amount: String(tx.outputs[0]?.token?.amount),
                category: binToHex(tx.outputs[0]?.token?.category)
            }
        };
    }
    /**
     * Get transaction template for mining photons. A template is a mining transaction with a zero nonce.
     *
     * @param now - The current bitcoin block height timestamp (expressed in blocks).
     * @param contractUtxo - contract outputs to use as input.
     * @param minerThrowawayKey - A private key used for signing the nonce message.
     * @param rewardAddress - The P2PKH Cashaddress to receive payouts.
     * @param category - The token category of the photons being mined.
     * @param fee - transaction fee to pay (per byte); default 1 sat/byte.
     *
     * @throws {Error} if transaction generation fails.
     * @returns a transaction template.
     */
    static mineDebug(now, contractUtxo, minerThrowawayKey, rewardAddress, category, nonce = 0) {
        const inputs = [];
        const outputs = [];
        let photonCat = category ? hexToBin(category) : PHOTON_CATEGORY;
        let age = contractUtxo.height <= 0 ? 0 : now - contractUtxo.height;
        const rewardAmount = Math.floor(Number(BigInt(contractUtxo.token_data.amount) / 420000n)) - 1;
        let config = {
            locktime: 0,
            version: 2,
            inputs,
            outputs
        };
        config.inputs.push(this.getInput(contractUtxo, age, minerThrowawayKey));
        const nextTargetTail = binToBigIntUintLE(this.getNextTarget(contractUtxo, now).slice(-16));
        let transaction;
        for (let nonceI = 0; nonceI < 1000; nonceI++) {
            config.outputs = [this.getOutput(contractUtxo, rewardAmount, now, minerThrowawayKey, nonce)];
            config.outputs.push(this.getRewardOutput(rewardAmount, rewardAddress, photonCat));
            let result = generateTransaction(config);
            if (!result.success)
                throw new Error('generate transaction failed!, errors: ' + JSON.stringify(result.errors, null, '  '));
            transaction = result.transaction;
            let templateBin = encodeTransactionBch(transaction);
            if (binToBigIntUintLE(hash256(templateBin).slice(-16)) < nextTargetTail) {
                const sourceOutputs = [this.getSourceOutput(contractUtxo)];
                let state = this.vm.debug({
                    inputIndex: 0,
                    sourceOutputs,
                    transaction,
                });
                let trace = stringifyDebugTraceSummary(summarizeDebugTrace(state.slice(-9)));
                console.log(trace);
                return binToHex(encodeTransactionBch(transaction));
            }
        }
        return;
    }
}
function incrementLEsubArray(arr) {
    for (let i = 0; i < arr.length; i++) {
        if (arr[i] === 255) {
            arr[i] = 0;
        }
        else {
            arr[i]++;
            break;
        }
    }
    return arr;
}
/**
     * Get transaction template for mining photons.
     *
     * @param minerThrowawayKey - A private key used for signing the nonce message.
     * @param rewardAddress - The P2PKH Cashaddress to receive payouts.
     * @param category - The token category of the photons being mined.
     * @param fee - transaction fee to pay (per byte); default 1 sat/byte.
     *
     * @throws {Error} if transaction generation fails.
     * @returns a transaction template.
     */
export async function mine(minerThrowawayKey, template) {
    const parentPrivateNode = decodeHdPrivateKey(minerThrowawayKey);
    if (typeof parentPrivateNode == "string")
        throw parentPrivateNode;
    let minerNodeChild = deriveHdPrivateNodeChild(parentPrivateNode.node, 0);
    const privateKeyChild = minerNodeChild.privateKey;
    const publicNode = deriveHdPublicNode(parentPrivateNode.node);
    const publicKey = deriveHdPublicNodeChild(publicNode, 0);
    const templateBin = hexToBin(template);
    // The index of return baton can change of the sequence (relative age) 
    // pushed has a different length
    //
    let BATON_START = template.indexOf("716400000000");
    if (BATON_START == -1)
        throw Error("could not locate baton in template");
    BATON_START = (BATON_START / 2) + 2;
    templateBin.set(publicKey.publicKey, 45);
    const HEARTBEAT_FREQ = 10000;
    const nextTarget = templateBin.slice(BATON_START + 4, BATON_START + 4 + 32);
    const nextTargetTail = binToBigIntUintLE(nextTarget.slice(-16));
    const initialJobStartNonce = 0; //Math.floor((Math.random() * (2 ** 31)))
    let oldNonce = initialJobStartNonce;
    let prevTime = performance.now();
    const nonceBin = numberToBinUint32LE(initialJobStartNonce);
    templateBin.set(nonceBin, BATON_START);
    // calculate an updated transaction
    for (let nonce = initialJobStartNonce; true; nonce++) {
        templateBin.set(incrementLEsubArray(templateBin.slice(BATON_START, BATON_START + 4)), BATON_START);
        templateBin.set(secp256k1.signMessageHashSchnorr(privateKeyChild, sha256.hash(templateBin.slice(BATON_START, BATON_START + (4 + 32)))), BATON_START + 36);
        if (nonce % HEARTBEAT_FREQ == 0) {
            postMessage({
                status: "STATUS_HEARTBEAT",
                timestamp: Date.now(),
                nonce: binToHex(templateBin.slice(BATON_START, BATON_START + 4)),
                target: binToHex(nextTarget),
                hashrate: Math.floor(HEARTBEAT_FREQ / ((performance.now() - prevTime) / 1000)),
                message: "still mining"
            });
            oldNonce = nonce;
            prevTime = performance.now();
        }
        if (binToBigIntUintLE(hash256(templateBin).slice(-16)) < nextTargetTail) {
            return (binToHex(templateBin));
        }
    }
}
//# sourceMappingURL=index.js.map